/**
 * Writes a note on an investigation, of a kind (PRD-0075 R-E4): an observation, an assessment —
 * a judgment in words, which shows on the hypothesis the investigation is evidence for — or a
 * task. Presentational: the feature stores the note and answers what went wrong, if anything.
 */
import { ListTodo, Loader2, Scale, StickyNote } from 'lucide-react'
import { type FormEvent, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { INVESTIGATION_NOTE_KINDS, type InvestigationNoteKind } from '@/types/investigation'

const COPY = 'investigations.page.note'

const KIND_ICON: Record<InvestigationNoteKind, typeof StickyNote> = {
  observation: StickyNote,
  assessment: Scale,
  task: ListTodo,
}

export interface NoteDraft {
  text: string
  kind: InvestigationNoteKind
}

export interface InvestigationNoteComposerProps {
  /** Stores the note; answers the message to show when it could not be stored. */
  onAdd: (draft: NoteDraft) => Promise<string | null>
  /** The investigation takes no note (read-only). */
  disabled?: boolean
}

export function InvestigationNoteComposer({
  onAdd,
  disabled = false,
}: InvestigationNoteComposerProps) {
  const { t } = useTranslation()
  const kindLabel = useId()
  const [text, setText] = useState('')
  const [kind, setKind] = useState<InvestigationNoteKind>('observation')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!text.trim() || pending) return
    setPending(true)
    const failed = await onAdd({ text: text.trim(), kind })
    setPending(false)
    setError(failed)
    if (!failed) setText('')
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-2 rounded-lg border border-border/60 p-3">
      <Textarea
        value={text}
        onChange={event => setText(event.target.value)}
        placeholder={t(`${COPY}.composer.placeholder`)}
        aria-label={t(`${COPY}.composer.label`)}
        disabled={disabled}
        className="min-h-16"
      />
      <div className="flex flex-wrap items-center gap-2">
        <span id={kindLabel} className="sr-only">
          {t(`${COPY}.composer.kind`)}
        </span>
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          aria-labelledby={kindLabel}
          value={kind}
          onValueChange={next => next && setKind(next as InvestigationNoteKind)}
          disabled={disabled}
        >
          {INVESTIGATION_NOTE_KINDS.map(option => {
            const Icon = KIND_ICON[option]
            return (
              <ToggleGroupItem
                key={option}
                value={option}
                className="data-[state=on]:border-primary/50 data-[state=on]:bg-primary/15 data-[state=on]:text-foreground"
              >
                <Icon aria-hidden />
                {t(`${COPY}.kinds.${option}`)}
              </ToggleGroupItem>
            )
          })}
        </ToggleGroup>
        <span className="flex-1" />
        <Button type="submit" size="sm" disabled={disabled || pending || !text.trim()}>
          {pending && <Loader2 aria-hidden className="animate-spin" />}
          {t(pending ? `${COPY}.composer.adding` : `${COPY}.composer.add`)}
        </Button>
      </div>
      {kind === 'assessment' && (
        <p className="text-2xs text-muted-foreground">{t(`${COPY}.composer.assessmentHint`)}</p>
      )}
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </form>
  )
}
